// Client for fetching a driver's MVR documents from IGIGDataFabricAPI (via the
// Trucking Portal AI API) and ingesting one without a manual download/upload.

export interface AvailableMvr {
  transactionId: string;
  type: string;               // "Activity History" | "Transactional MVR"
  driverName?: string | null;
  licenseNumber?: string | null;
  status?: string | null;
  /** False when the order produced no document — a failed or pending order. */
  importable?: boolean;
  /** True while the order is still with the vendor — status can be refreshed. */
  pending?: boolean;
  /** True when this document is already on the quote. */
  imported?: boolean;
  /** True when the order's status can be re-checked. */
  refreshable?: boolean;
  orderId?: string | null;
  flow?: string | null;
  created?: string | null;
}

export interface DriverSearch {
  licenseNumber?: string;
  firstName?: string;
  lastName?: string;
}

function apiBase(): string {
  return process.env.REACT_APP_TRUCKING_PORTAL_AI_API_Url ?? '';
}


function authHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('igtoken') || ''}`,
    'X-NC-Token': localStorage.getItem('NCToken') || '',
  };
}

/** Extract the real error message from a FastAPI response body (detail), with fallbacks. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json();
    const detail = data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const msg = detail.map((d: any) => d?.msg || (typeof d === 'string' ? d : '')).filter(Boolean).join('; ');
      if (msg) return msg;
    }
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  } catch {
    // body wasn't JSON — fall through
  }
  return `Request failed (${res.status}${res.statusText ? ' ' + res.statusText : ''}).`;
}

/** List a driver's already-ordered MVR documents (with their type). */
export async function listAvailableMvr(quoteId: string, driver: DriverSearch): Promise<AvailableMvr[]> {
  const res = await fetch(`${apiBase()}/ai/mvr/available`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ quoteId, ...driver }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  const data = await res.json();
  return (data?.documents as AvailableMvr[]) ?? [];
}

/** Ingest a chosen MVR by transactionId — server resolves the PDF and runs the pipeline. */
export async function ingestMvr(quoteId: string, transactionId: string, driverName?: string): Promise<any> {
  const res = await fetch(`${apiBase()}/ai/mvr/available/ingest`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ quoteId, transactionId, driverName }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}

/** A driver record parsed from an Activity History report, as the Toolbox shows it. */
export interface MvrRecord {
  person?: Record<string, any> | null;
  address?: Record<string, any> | null;
  violations: Record<string, any>[];
  accidents: Record<string, any>[];
  licences: Record<string, any>[];
  reportDateTime?: string | null;
}

export interface MvrPreview {
  transactionId: string;
  type: string;
  /** Activity History — parsed sections. */
  record?: MvrRecord | null;
  /** Transactional MVR — short-lived URL to the PDF. */
  documentUrl?: string | null;
}

/** Fetch an MVR for display without importing it. */
export async function previewMvr(quoteId: string, transactionId: string): Promise<MvrPreview> {
  const res = await fetch(`${apiBase()}/ai/mvr/available/preview`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ quoteId, transactionId }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json();
}
